import { useState, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import { IDLE_TIMEOUT_MINUTES } from "@/lib/constants";
import { can, homeForRole } from "@/lib/roles";
import Sidebar from "@/components/Sidebar";
import BookAppointmentDialog from "@/components/BookAppointmentDialog";
import ClinicalNoteDialog from "@/components/ClinicalNoteDialog";
import ReceiptModal from "@/components/ReceiptModal";
import DoctorFormDialog from "@/components/DoctorFormDialog";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";
import ConfirmDialog from "@/components/ConfirmDialog";
import DashboardHeader from "@/components/DashboardHeader";
import DashboardContent from "@/components/DashboardContent";
import { useAuth } from "@/context/AuthContext";
import { useIdleLogout } from "@/hooks/useIdleLogout";
import { useDashboardData } from "@/hooks/useDashboardData";
import { toast } from "sonner";

const fmtDate = (s) =>
  s ? new Date(s).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

export default function Dashboard() {
  const { user, logout, refreshUser } = useAuth();
  const role = user?.role || "admin";
  const [active, setActive] = useState(homeForRole(role));
  const [query, setQuery] = useState("");
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [booking, setBooking] = useState(null); // { editing, prefill, reqId }
  const [clinical, setClinical] = useState(null);
  const [receipt, setReceipt] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [doctorDialog, setDoctorDialog] = useState(null); // { editing }
  const [deleteDoctorId, setDeleteDoctorId] = useState(null);
  const [pwdOpen, setPwdOpen] = useState(false);
  const [userFormOpen, setUserFormOpen] = useState(false);
  const forcePwd = !!user?.must_change_password;
  const data = useDashboardData({ query, selectedDate, enabled: !forcePwd, role });
  const { doctors, load, run } = data;

  const idleLogout = useCallback(() => { logout(); toast.info(`Signed out after ${IDLE_TIMEOUT_MINUTES} minutes of inactivity`); }, [logout]);
  useIdleLogout(idleLogout, IDLE_TIMEOUT_MINUTES);

  const openBooking = (editing = null, prefill = null, reqId = null) => setBooking({ editing, prefill, reqId });

  const handleApptSaved = async () => {
    if (booking?.reqId) {
      try { await api.patch(`/booking-requests/${booking.reqId}`, { status: "done" }); }
      catch (e) { toast.error(apiError(e, "Appointment saved, but the request could not be marked done")); }
    }
    load();
  };

  const actions = {
    editDoctor: (d) => setDoctorDialog({ editing: d }),
    deleteDoctor: setDeleteDoctorId,
    convertRequest: (r) => {
      const doc = doctors.find((d) => d.id === r.doctor_id);
      openBooking(null, { doctor_id: r.doctor_id, patient_name: r.patient_name, phone: r.phone || "", amount: doc ? String(doc.fee) : "" }, r.id);
    },
    setReqStatus: (id, status) => run(() => api.patch(`/booking-requests/${id}`, { status }), null, "Could not update request"),
    deleteRequest: (id) => run(() => api.delete(`/booking-requests/${id}`), "Request removed", "Could not remove request"),
  };
  const confirmDelete = () => run(() => api.delete(`/appointments/${deleteId}`).then(() => setDeleteId(null)), "Appointment deleted", "Delete failed");
  const confirmDeleteDoctor = () => run(() => api.delete(`/doctors/${deleteDoctorId}`).then(() => setDeleteDoctorId(null)), "Doctor removed", "Delete failed");

  const perms = { update: can(role, "appointments:update"), delete: can(role, "appointments:delete"), clinical: can(role, "appointments:clinical") };
  const tableProps = { fmtDate, openEdit: (a) => openBooking(a), openClinical: setClinical, setDeleteId, setReceipt, perms, testid: "appointment-list-table",
    confirmAppt: (a) => run(() => api.post(`/appointments/${a.id}/confirm`), `Confirmed ${a.patient_name}'s booking`, "Could not confirm") };

  return (
    <div className="flex bg-[#F8FAFC] min-h-screen">
      <Sidebar active={active} onChange={setActive} badges={data.badges} onChangePassword={() => setPwdOpen(true)} />
      <main className="flex-1 min-w-0">
        <DashboardHeader active={active} onNav={setActive} role={role} onAddDoctor={() => setDoctorDialog({ editing: null })}
          onNewAppointment={() => openBooking()} onAddUser={() => setUserFormOpen(true)} />
        <div className="p-5 sm:p-8 space-y-8">
          <DashboardContent active={active} data={data} query={query} setQuery={setQuery} selectedDate={selectedDate} setSelectedDate={setSelectedDate}
            tableProps={tableProps} fmtDate={fmtDate} actions={actions} role={role} user={user} userForm={{ open: userFormOpen, setOpen: setUserFormOpen }} />
        </div>
      </main>

      <BookAppointmentDialog open={!!booking} onOpenChange={(v) => !v && setBooking(null)} doctors={doctors} onSaved={handleApptSaved}
        editing={booking?.editing ?? null} prefill={booking?.prefill ?? null} />
      <ClinicalNoteDialog appointment={clinical} onOpenChange={(v) => !v && setClinical(null)} onSaved={load} />
      <ReceiptModal open={!!receipt} onOpenChange={(v) => !v && setReceipt(null)} appointment={receipt} />
      <DoctorFormDialog open={!!doctorDialog} onOpenChange={(v) => !v && setDoctorDialog(null)} onSaved={load} editing={doctorDialog?.editing ?? null} />
      <ChangePasswordDialog open={pwdOpen || forcePwd} onOpenChange={setPwdOpen} forced={forcePwd} onChanged={refreshUser} />

      <ConfirmDialog open={!!deleteId} onOpenChange={(v) => !v && setDeleteId(null)} title="Delete appointment?"
        description="This cannot be undone." actionLabel="Delete" onConfirm={confirmDelete} testid="confirm-delete-button" />
      <ConfirmDialog open={!!deleteDoctorId} onOpenChange={(v) => !v && setDeleteDoctorId(null)} title="Remove this doctor?"
        description="They will no longer appear on the landing page. This cannot be undone." actionLabel="Remove"
        onConfirm={confirmDeleteDoctor} testid="confirm-delete-doctor-button" />
    </div>
  );
}
